import {
  LocalStackEventType,
  getThemeOptionsFromType,
  ThemeType,
  useObservableEvent,
  getStoredThemeType,
  EventDataMap,
} from '@localstack/integrations';
import { ReactElement, useEffect, useRef } from 'react';

import { createStyles, makeStyles } from '@mui/styles';
import { TEST_IDS } from '@localstack/constants';

const useStyles = makeStyles(() =>
  createStyles({
    fullFrame: {
      width: '100%',
      height: '100%',
      border: 'none',
    },
  }),
);

type IFrameExtensionProps = {
  url: string;
};

export const IframeExtension = ({ url }: IFrameExtensionProps): ReactElement => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const classes = useStyles();

  const postThemeInject = (theme: ThemeType) => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        eventType: LocalStackEventType.THEME_INJECT,
        data: { theme: JSON.stringify(getThemeOptionsFromType(theme)) },
      },
      '*',
    );
  };

  const injectTheme = () => postThemeInject(getStoredThemeType());

  const handleThemeUpdate = (event: EventDataMap[LocalStackEventType.THEME_UPDATE]) => {
    // we inject, not update the theme
    postThemeInject(event.theme);
  };

  const handleSetHeight = (event: EventDataMap[LocalStackEventType.SET_HEIGHT]) => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }
    iframe.style.height = event.height;
  };

  useObservableEvent({
    subscribedEvents: {
      [LocalStackEventType.THEME_UPDATE]: handleThemeUpdate,
      [LocalStackEventType.SET_HEIGHT]: handleSetHeight,
    },
  });

  useEffect(() => {
    const iframe = iframeRef.current;
    const onLoad = () => injectTheme();
    iframe?.addEventListener('load', onLoad);
    return () => iframe?.removeEventListener('load', onLoad);
  }, []);

  return (
    <iframe
      data-testid={TEST_IDS.IFRAME_EXTENSION}
      ref={iframeRef}
      // eslint-disable-next-line @eslint-react/dom/no-unsafe-iframe-sandbox
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      src={url}
      title="Embedded Iframe"
      className={classes.fullFrame}
    />
  );
};
